import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskedText } from "../lib/motion";

const SLIDES = [
  {
    id: "s1",
    tag: "Founder in the Classroom",
    titleLines: ["Teaching, One", "Classroom", "at a Time."],
    body: "Karnataka Kalashree Smt. Revathi Kamath spends her days inside the rural schools we adopt — sitting with teachers, listening to children, and building change from the classroom outward.",
    cta: "Read Her Story",
    ctaHref: "/about#founder",
    image: "/images/founder-classroom.jpg",
  },
  {
    id: "s2",
    tag: "Works Under Progress",
    titleLines: ["Work Begins", "at Hallagere."],
    body: "The rebuild of Parinitha Aided Higher Primary School, Hallagere (Mandya) is now underway — three new classrooms, hygienic toilets, RO drinking water and a revived playground.",
    cta: "See the Hallagere Works",
    ctaHref: "/causes#works",
    image: "/images/halegere-1.jpg",
  },
];

const EASE = [0.22, 1, 0.36, 1];

export default function Hero() {
  const [idx, setIdx] = useState(0);
  const total = SLIDES.length;
  const sectionRef = useRef(null);
  const go = useCallback((n) => setIdx((n + total) % total), [total]);

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % total), 6500);
    return () => clearInterval(t);
  }, [total]);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.16]);

  const s = SLIDES[idx];

  return (
    <section
      id="home"
      ref={sectionRef}
      data-testid="hero-section"
      className="relative pb-0 overflow-hidden bg-white w-full"
    >
      {/* floating accent orbs */}
      <div className="pointer-events-none absolute -top-10 left-[35%] w-72 h-72 rounded-full bg-teal/10 blur-3xl" />
      <div className="pointer-events-none absolute top-40 -left-16 w-64 h-64 rounded-full bg-coral/10 blur-3xl" />

      <div className="grid lg:grid-cols-12 gap-0">
        <div className="lg:col-span-6 px-4 sm:px-8 lg:pl-12 lg:pr-8 pb-14 md:pb-16 pt-8 lg:pt-4 flex flex-col justify-center order-2 lg:order-1 min-w-0">
          <div className="max-w-2xl relative z-10 text-center md:text-left mx-auto md:mx-0 min-w-0">
            <motion.span
              key={s.id + "-chip"}
              className="section-chip"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: EASE }}
            >
              {s.tag}
            </motion.span>

            <h1
              data-testid="hero-headline"
              className="mt-5 md:mt-6 font-display text-navy font-extrabold text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl leading-[1.05]"
            >
              <MaskedText
                keyProp={s.id}
                lines={s.titleLines}
                delay={0.15}
                stagger={0.11}
              />
            </h1>

            <motion.p
              key={s.id + "-p"}
              className="mt-5 md:mt-6 text-base sm:text-lg md:text-xl text-navy/70 max-w-xl mx-auto md:mx-0 font-display leading-relaxed"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: EASE, delay: 0.5 }}
            >
              {s.body}
            </motion.p>

            <motion.div
              className="mt-8 flex flex-wrap items-center gap-3 sm:gap-4 justify-center md:justify-start"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: EASE, delay: 0.65 }}
            >
              <Link to={s.ctaHref} data-testid="hero-cta-primary" className="btn-vermilion group">
                {s.cta}
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link to="/get-involved#volunteer" className="btn-outline-indigo" data-testid="hero-cta-secondary">
                Volunteer
              </Link>
            </motion.div>

            <div className="mt-8 flex items-center gap-5 justify-center md:justify-start">
              <button
                data-testid="hero-prev"
                onClick={() => go(idx - 1)}
                className="w-11 h-11 border-2 border-navy text-navy flex items-center justify-center hover:bg-navy hover:text-white transition-colors rounded-sm"
                aria-label="Previous slide"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="flex gap-2">
                {SLIDES.map((_, i) => (
                  <button
                    key={i}
                    data-testid={`hero-dot-${i}`}
                    onClick={() => go(i)}
                    className={`h-[3px] transition-all duration-500 ${i === idx ? "w-10 bg-teal" : "w-5 bg-navy/20"}`}
                    aria-label={`Go to slide ${i + 1}`}
                  />
                ))}
              </div>
              <button
                data-testid="hero-next"
                onClick={() => go(idx + 1)}
                className="w-11 h-11 border-2 border-navy text-navy flex items-center justify-center hover:bg-navy hover:text-white transition-colors rounded-sm"
                aria-label="Next slide"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        <div className="lg:col-span-6 order-1 lg:order-2 relative min-w-0">
          <div className="relative h-[280px] sm:h-[400px] md:h-[460px] lg:h-full lg:min-h-[520px] overflow-hidden">
            <motion.img
              key={s.id + "-img"}
              src={s.image}
              alt={s.titleLines.join(" ")}
              loading="eager"
              style={{ y: imgY, scale: imgScale }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.9, ease: EASE }}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-l from-transparent via-transparent to-white/10 pointer-events-none" />
            <motion.div
              className="absolute bottom-6 right-6 hidden md:flex items-center gap-2 bg-teal text-white px-4 py-3 shadow-teal"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: EASE, delay: 0.8 }}
            >
              <div className="stat-num text-2xl leading-none">100</div>
              <div className="text-[10px] uppercase font-display font-bold tracking-widest">Visionaries<br/>Goal</div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
